/**
 * samplingEditDoApi — RTK Query slice for the Quality › Sampling Data "Edit DO"
 * bulk-edit grid (Access `frmQualityQrySampling_EditMany_Add` hosting subform
 * `frmQualityQrySampling_EditMany`).
 *
 * Access flow:
 *   - Form_Load loads every eligible sample (sample_status='In Use' AND
 *     result='Accept', id DESC) into the grid, each with an editable `do_no`
 *     (RowSource `SELECT do_no FROM delivery_order_scs WHERE is_status=0`) and a
 *     `checked_row` tick.
 *   - btnSave_Click loops the ticked rows and UPDATEs `sampling.do_no` for each.
 *
 * → On the web there is no tmp table: GET returns the eligible samples + do_no
 *   option list; PUT applies the batch UPDATE for the ticked rows only.
 *
 * Reuses the shared `QualitySampling` cache tag so the bulk update invalidates
 * the Sampling Data list (Access `…!subForm.Requery`). Tag already registered on
 * apiSlice by the sibling quality_sampling module — NOT added here.
 */
import { apiSlice } from './apiSlice'

/** One eligible sample row for the bulk-edit grid (camelCase). */
export interface SamplingEditDoRow {
  /** sampling.id — PK targeted by the UPDATE. */
  id: number
  sampleNo: string | null
  tlcContractRef: string | null
  /** do_no — editable per row. */
  doNo: string | null
  lotId: string | null
  clientLotId: string | null
  grade: string | null
  consignee: string | null
  result: string | null
}

export interface SamplingEditDoListResponse {
  rows: SamplingEditDoRow[]
  doOptions: string[]
}

/** One ticked row's editable payload for the batch UPDATE. */
export interface SamplingEditDoUpdateItem {
  id: number
  doNo: string | null
}

export interface SamplingEditDoUpdateRequest {
  rows: SamplingEditDoUpdateItem[]
}

const samplingEditDoApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getSamplesForEditDo: builder.query<SamplingEditDoListResponse, void>({
      query: () => ({ url: '/quality-sampling-edit-do' }),
      keepUnusedDataFor: 0,
    }),
    bulkUpdateSamplingDoNo: builder.mutation<{ updated: number }, SamplingEditDoUpdateRequest>({
      query: (body) => ({ url: '/quality-sampling-edit-do', method: 'PUT', body }),
      invalidatesTags: ['QualitySampling'],
    }),
  }),
})

export const {
  useGetSamplesForEditDoQuery,
  useBulkUpdateSamplingDoNoMutation,
} = samplingEditDoApi

export default samplingEditDoApi
